import React, { useState } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';
import DetailModal from '../components/DetailModal';

const GenericCardView = ({ title, icon: Icon, colorClass, data, type, isAdmin, filterFn, defaultData, showToast }) => {
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    
    const rawData = type === 'ARTIFACT' ? data.artifacts : data.ideas;
    const displayData = filterFn ? (rawData || []).filter(filterFn) : (rawData || []);
    const collectionName = type === 'ARTIFACT' ? 'artifacts' : 'ideas';

    const handleSave = async (formData) => {
        const payload = { ...formData, status: formData.status || 'DRAFT' };
        try {
            const coll = collection(db, 'artifacts', appId, 'public', 'data', collectionName);
            if (formData.id) await updateDoc(doc(coll, formData.id), payload);
            else await addDoc(coll, { ...payload, timestamp: serverTimestamp() });
            
            setEditing(null); setIsAdding(false);
            showToast("Record successfully saved.", "success");
        } catch (err) { showToast("Firebase Error: Cannot save record.", "error"); }
    };

    const handleDelete = async (e, item) => { 
        e.stopPropagation();
        if (!window.confirm(`Permanently purge ${item.title || item.name}?`)) return;
        try { 
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', collectionName, item.id)); 
            showToast("Record successfully purged.", "success");
        } catch (e) { showToast("Firebase Error.", "error"); } 
    };
    
    const borderColor = colorClass.replace('text-', 'border-').replace('600', '900/50').replace('500', '900/50');
    const hoverColor = colorClass.replace('text-', 'hover:border-').replace('600', '500').replace('500', '400');

    return (
        <div className="flex-1 overflow-y-auto p-6 bg-[#050505] custom-scrollbar fade-in h-full pb-24">
            <div className="flex justify-between items-center mb-6 border-b border-gray-900 pb-4">
                <h2 className="text-xl font-bold flex items-center gap-2 text-white font-sans tracking-tight">
                    <Icon className={colorClass} /> {title}
                </h2>
                {isAdmin && (
                    <button onClick={() => setIsAdding(true)} className={`bg-transparent ${colorClass} border ${borderColor} px-3 py-1.5 rounded text-[10px] font-bold tracking-wider flex items-center gap-2 hover:bg-white/5 transition-colors`}>
                        <Icons.Plus size={12} /> ADD
                    </button>
                )}
            </div>

            {displayData.length === 0 ? (
                <div className="text-gray-600 text-sm font-mono mt-10 text-center border border-dashed border-gray-800 p-10">NO RECORDS DETECTED IN THIS ARCHIVE.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch">
                    {displayData.map(item => (
                        <div key={item.id} onClick={() => setSelected(item)} className={`bg-[#0a0a0a] border border-gray-900 p-4 rounded ${hoverColor} transition-colors group relative cursor-pointer min-h-[14rem] h-full flex flex-col shadow-lg ${item.status === 'DRAFT' ? 'opacity-80' : ''}`}>
                            
                            {item.imageUrl && (
                                <div className="h-40 w-full mb-4 bg-black rounded border border-gray-800 overflow-hidden flex items-center justify-center p-2">
                                    <img src={item.imageUrl} className="max-w-full max-h-full object-contain" alt="" />
                                </div>
                            )}
                            
                            <div className="relative z-10 flex flex-col flex-1">
                                <div className="flex justify-between items-start mb-1">
                                    <div className="flex flex-col gap-1 pr-2">
                                        <h3 className="font-bold text-white text-lg leading-tight">{item.title || item.name}</h3>
                                        <div className="flex flex-wrap mt-1 gap-1">
                                            {(!item.status || item.status === 'DRAFT') 
                                                ? <span className="bg-yellow-900/30 text-yellow-500 border border-yellow-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse"></span> DRAFT</span>
                                                : <span className="bg-green-900/20 text-green-500 border border-green-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">VERIFIED</span>
                                            }
                                            {/* NEW: Audio, Video, and 3D Badges */}
                                            {item.model3dUrl && <span className="bg-cyan-900/30 text-cyan-400 border border-cyan-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase flex items-center gap-1"><Icons.Box size={8} /> 3D READY</span>}
                                            {item.videoUrl && <span className="bg-purple-900/30 text-purple-400 border border-purple-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase flex items-center gap-1"><Icons.Play size={8} /> VIDEO</span>}
                                            {item.audioUrl && <span className="bg-pink-900/30 text-pink-400 border border-pink-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase flex items-center gap-1"><Icons.Music size={8} /> AUDIO</span>}
                                        </div>
                                    </div>
                                    {isAdmin && <button type="button" onClick={(e) => handleDelete(e, item)} className="text-gray-600 hover:text-red-500 shrink-0 ml-2 transition-colors bg-black border border-gray-800 rounded p-1" title="Delete"><Icons.Trash2 size={14} /></button>}
                                </div>
                                
                                <div className="flex flex-col gap-2 mb-4 mt-2">
                                    {item.code && <span className={`text-[9px] text-yellow-400 px-1.5 py-1 rounded border border-yellow-900/50 font-bold font-mono uppercase tracking-widest w-fit bg-black/50`}>{item.code}</span>}
                                    {item.assetId && <span className={`text-[9px] text-blue-400 px-1.5 py-1 rounded border border-blue-900/50 font-bold font-mono uppercase tracking-widest w-fit bg-black/50`}>ASSET ID: {item.assetId}</span>}
                                </div>
                                
                                <div className="mt-auto">
                                    {/* Removing raw HTML from grid preview by grabbing only the raw text */}
                                    {(item.desc || item.lore) && (
                                        <div className="border-t border-gray-800 pt-3">
                                            {item.desc && <div className="text-xs text-gray-400 leading-relaxed font-sans line-clamp-3 mb-2" dangerouslySetInnerHTML={{ __html: item.desc.replace(/<[^>]*>?/gm, '') }} />}
                                            {item.lore && <div className="text-xs text-green-500/80 leading-relaxed font-sans border-l-2 border-green-900/50 pl-2 mt-2 line-clamp-2" dangerouslySetInnerHTML={{ __html: item.lore.replace(/<[^>]*>?/gm, '') }} />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {selected && (
                <DetailModal item={selected} type={type} isAdmin={isAdmin} onClose={() => setSelected(null)} onEdit={(item) => { setSelected(null); setTimeout(() => setEditing(item), 100); }} />
            )}

            {(isAdding || editing) && (
                <AddDataModal type={type} initialData={isAdding ? (defaultData || {}) : editing} onClose={() => { setIsAdding(false); setEditing(null); }} onSubmit={handleSave} showToast={showToast} isAdmin={isAdmin} />
            )}
        </div>
    );
};

export default GenericCardView;